import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { useDataContext } from '../../contexts/DataContext';
import { 
  Building2, CheckCircle, XCircle, Save, ChevronDown, ChevronRight, 
  Search, Settings, Eye, PlayCircle, MessageSquare, Filter,
  BarChart3, Users, Shield, Smartphone, Zap, HelpCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const TenantPermissions = () => {
  const { getMockData, updateMockData } = useDataContext();
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  
  // Category icons
  const categoryIcons = {
    view: Eye,
    operation: PlayCircle,
    communication: MessageSquare,
    config: Settings,
    manage: Settings,
    review: Eye,
    report: BarChart3,
    analytics: BarChart3,
    monitor: Eye,
    collaborate: MessageSquare,
    access: Smartphone,
    automate: Zap,
    ai: Zap,
    develop: Settings,
    support: HelpCircle,
    track: Eye
  };

  // All available modules/screens - Detailed Modular Structure
  const modules = [
    {
      module: 'Ticket Management',
      subModules: [
        {
          subModule: 'Ticket Views & Queues',
          screens: [
            { screen: 'ticket-dashboard', name: 'Ticket Dashboard', description: 'Overview of all tickets', category: 'view' },
            { screen: 'ticket-queue', name: 'Ticket Queue', description: 'Unassigned tickets queue', category: 'view' },
            { screen: 'my-tickets', name: 'My Assigned Tickets', description: 'Personal assigned tickets', category: 'view' },
            { screen: 'team-tickets', name: 'Team Tickets', description: 'View team member tickets', category: 'view' },
            { screen: 'all-tickets', name: 'All Tickets View', description: 'Complete ticket listing', category: 'view' },
            { screen: 'closed-tickets', name: 'Closed Tickets', description: 'Historical closed tickets', category: 'view' },
            { screen: 'escalated-tickets', name: 'Escalated Tickets', description: 'Tickets requiring attention', category: 'view' },
            { screen: 'sla-breach-tickets', name: 'SLA Breach Tickets', description: 'Tickets exceeding SLA', category: 'view' },
          ]
        },
        {
          subModule: 'Ticket Operations',
          screens: [
            { screen: 'ticket-create', name: 'Create Tickets', description: 'Create new tickets', category: 'operation' },
            { screen: 'ticket-assignment', name: 'Assign Tickets', description: 'Assign to agents/teams', category: 'operation' },
            { screen: 'ticket-reassignment', name: 'Reassign Tickets', description: 'Change assignment', category: 'operation' },
            { screen: 'ticket-escalation', name: 'Escalate Tickets', description: 'Escalate to higher level', category: 'operation' },
            { screen: 'ticket-resolution', name: 'Resolve Tickets', description: 'Mark tickets as resolved', category: 'operation' },
            { screen: 'ticket-close', name: 'Close Tickets', description: 'Close completed tickets', category: 'operation' },
            { screen: 'ticket-reopen', name: 'Reopen Tickets', description: 'Reopen closed tickets', category: 'operation' },
          ]
        },
        {
          subModule: 'Ticket Communication',
          screens: [
            { screen: 'ticket-comment', name: 'Add Comments', description: 'Internal ticket comments', category: 'communication' },
            { screen: 'ticket-note', name: 'Add Private Notes', description: 'Private agent notes', category: 'communication' },
            { screen: 'ticket-reply', name: 'Reply to Customers', description: 'Customer communication', category: 'communication' },
            { screen: 'ticket-cc', name: 'CC/Email Management', description: 'Manage email recipients', category: 'communication' },
            { screen: 'ticket-attachment', name: 'Attach Files', description: 'Upload/download attachments', category: 'communication' },
          ]
        },
        {
          subModule: 'Ticket Configuration',
          screens: [
            { screen: 'ticket-types', name: 'Ticket Types', description: 'Configure ticket types', category: 'config' },
            { screen: 'ticket-statuses', name: 'Ticket Statuses', description: 'Manage status workflows', category: 'config' },
            { screen: 'ticket-priorities', name: 'Ticket Priorities', description: 'Configure priority levels', category: 'config' },
            { screen: 'ticket-categories', name: 'Ticket Categories', description: 'Organize by category', category: 'config' },
            { screen: 'ticket-tags', name: 'Ticket Tags', description: 'Manage ticket tags', category: 'config' },
            { screen: 'ticket-forms', name: 'Custom Forms', description: 'Create ticket forms', category: 'config' },
          ]
        }
      ]
    },
    {
      module: 'Knowledge Base',
      subModules: [
        {
          subModule: 'Article Management',
          screens: [
            { screen: 'kb-overview', name: 'KB Dashboard', description: 'Overview of articles', category: 'view' },
            { screen: 'article-management', name: 'Article Management', description: 'Manage all articles', category: 'manage' },
            { screen: 'my-articles', name: 'My Articles', description: 'Personal articles', category: 'view' },
            { screen: 'article-create', name: 'Create Articles', description: 'Create new articles', category: 'operation' },
            { screen: 'article-edit', name: 'Edit Articles', description: 'Modify existing articles', category: 'operation' },
            { screen: 'article-delete', name: 'Delete Articles', description: 'Remove articles', category: 'operation' },
            { screen: 'article-publish', name: 'Publish Articles', description: 'Publish to KB', category: 'operation' },
          ]
        },
        {
          subModule: 'Article Review & Approval',
          screens: [
            { screen: 'pending-review', name: 'Pending Review', description: 'Articles awaiting review', category: 'review' },
            { screen: 'article-approve', name: 'Approve Articles', description: 'Approve for publication', category: 'review' },
            { screen: 'article-reject', name: 'Reject Articles', description: 'Reject articles', category: 'review' },
            { screen: 'published-articles', name: 'Published Articles', description: 'Live KB articles', category: 'view' },
            { screen: 'article-revisions', name: 'View Revisions', description: 'Article version history', category: 'review' },
          ]
        },
        {
          subModule: 'KB Configuration',
          screens: [
            { screen: 'kb-categories', name: 'KB Categories', description: 'Organize articles', category: 'config' },
            { screen: 'kb-templates', name: 'Article Templates', description: 'Create templates', category: 'config' },
            { screen: 'kb-settings', name: 'KB Settings', description: 'Configure KB', category: 'config' },
          ]
        }
      ]
    },
    {
      module: 'Reports & Analytics',
      subModules: [
        {
          subModule: 'Basic Reports',
          screens: [
            { screen: 'reports', name: 'Reports Dashboard', description: 'Access all reports', category: 'view' },
            { screen: 'ticket-reports', name: 'Ticket Reports', description: 'Ticket analytics', category: 'report' },
            { screen: 'agent-reports', name: 'Agent Reports', description: 'Agent performance', category: 'report' },
            { screen: 'team-reports', name: 'Team Reports', description: 'Team statistics', category: 'report' },
            { screen: 'sla-reports', name: 'SLA Reports', description: 'Service level reports', category: 'report' },
          ]
        },
        {
          subModule: 'Advanced Analytics',
          screens: [
            { screen: 'agent-performance', name: 'Agent Performance', description: 'Detailed agent metrics', category: 'analytics' },
            { screen: 'advanced-analytics', name: 'Advanced Analytics', description: 'Deep dive analysis', category: 'analytics' },
            { screen: 'trend-analysis', name: 'Trend Analysis', description: 'Historical trends', category: 'analytics' },
            { screen: 'custom-reports', name: 'Custom Reports', description: 'Build custom reports', category: 'analytics' },
            { screen: 'export-reports', name: 'Export Reports', description: 'Export data', category: 'operation' },
          ]
        },
        {
          subModule: 'Real-time Monitoring',
          screens: [
            { screen: 'real-time-dashboard', name: 'Real-time Dashboard', description: 'Live metrics', category: 'monitor' },
            { screen: 'alert-management', name: 'Alert Management', description: 'Configure alerts', category: 'monitor' },
          ]
        }
      ]
    },
    {
      module: 'Problem Management',
      subModules: [
        {
          subModule: 'Problem Operations',
          screens: [
            { screen: 'problem-dashboard', name: 'Problem Dashboard', description: 'View all problems', category: 'view' },
            { screen: 'problem-create', name: 'Create Problem', description: 'Create new problem', category: 'operation' },
            { screen: 'problem-assign', name: 'Assign Problem', description: 'Assign to analyst', category: 'operation' },
            { screen: 'problem-resolve', name: 'Resolve Problem', description: 'Mark as resolved', category: 'operation' },
            { screen: 'problem-analysis', name: 'Root Cause Analysis', description: 'Investigate causes', category: 'operation' },
            { screen: 'problem-close', name: 'Close Problem', description: 'Close resolved problems', category: 'operation' },
          ]
        },
        {
          subModule: 'Problem Tracking',
          screens: [
            { screen: 'problem-notes', name: 'Problem Notes', description: 'Add notes', category: 'operation' },
            { screen: 'problem-affected', name: 'Affected Items', description: 'Link affected tickets', category: 'track' },
            { screen: 'problem-status', name: 'Status Tracking', description: 'Track problem status', category: 'track' },
          ]
        }
      ]
    },
    {
      module: 'User & Team Management',
      subModules: [
        {
          subModule: 'User Management',
          screens: [
            { screen: 'users-dashboard', name: 'Users Dashboard', description: 'View all users', category: 'view' },
            { screen: 'user-create', name: 'Create Users', description: 'Add new users', category: 'operation' },
            { screen: 'user-edit', name: 'Edit Users', description: 'Modify user info', category: 'operation' },
            { screen: 'user-delete', name: 'Delete Users', description: 'Remove users', category: 'operation' },
            { screen: 'user-roles', name: 'Assign Roles', description: 'Manage user roles', category: 'operation' },
            { screen: 'user-permissions', name: 'User Permissions', description: 'Configure permissions', category: 'operation' },
          ]
        },
        {
          subModule: 'Team Management',
          screens: [
            { screen: 'teams-dashboard', name: 'Teams Dashboard', description: 'View teams', category: 'view' },
            { screen: 'team-create', name: 'Create Teams', description: 'Create teams', category: 'operation' },
            { screen: 'team-edit', name: 'Edit Teams', description: 'Modify teams', category: 'operation' },
            { screen: 'team-delete', name: 'Delete Teams', description: 'Remove teams', category: 'operation' },
            { screen: 'team-members', name: 'Manage Members', description: 'Add/remove members', category: 'operation' },
          ]
        },
        {
          subModule: 'Agent Management',
          screens: [
            { screen: 'agent-training', name: 'Training Programs', description: 'Manage training', category: 'manage' },
            { screen: 'agent-profile', name: 'Agent Profiles', description: 'View profiles', category: 'view' },
            { screen: 'agent-settings', name: 'Agent Settings', description: 'Configure settings', category: 'manage' },
            { screen: 'agent-onboarding', name: 'Onboarding', description: 'New agent setup', category: 'manage' },
          ]
        }
      ]
    },
    {
      module: 'Admin & Configuration',
      subModules: [
        {
          subModule: 'System Settings',
          screens: [
            { screen: 'system-settings', name: 'System Settings', description: 'Global settings', category: 'config' },
            { screen: 'email-settings', name: 'Email Settings', description: 'Email configuration', category: 'config' },
            { screen: 'notification-settings', name: 'Notifications', description: 'Notification rules', category: 'config' },
            { screen: 'security-settings', name: 'Security Settings', description: 'Security policies', category: 'config' },
          ]
        },
        {
          subModule: 'SLA Configuration',
          screens: [
            { screen: 'sla-policies', name: 'SLA Policies', description: 'Define SLAs', category: 'config' },
            { screen: 'sla-metrics', name: 'SLA Metrics', description: 'Track metrics', category: 'config' },
            { screen: 'business-hours', name: 'Business Hours', description: 'Working hours', category: 'config' },
          ]
        },
        {
          subModule: 'Automation & Workflows',
          screens: [
            { screen: 'automation-rules', name: 'Automation Rules', description: 'Configure automation', category: 'config' },
            { screen: 'workflows', name: 'Workflows', description: 'Ticket workflows', category: 'config' },
            { screen: 'triggers', name: 'Triggers', description: 'Event triggers', category: 'config' },
            { screen: 'conditions', name: 'Conditions', description: 'Rule conditions', category: 'config' },
          ]
        },
        {
          subModule: 'Ticket Configuration',
          screens: [
            { screen: 'ticket-types', name: 'Ticket Types', description: 'Configure types', category: 'config' },
            { screen: 'ticket-statuses', name: 'Status Workflows', description: 'Status management', category: 'config' },
            { screen: 'ticket-priorities', name: 'Priority Levels', description: 'Priority settings', category: 'config' },
            { screen: 'ticket-categories', name: 'Categories', description: 'Category management', category: 'config' },
            { screen: 'custom-fields', name: 'Custom Fields', description: 'Create fields', category: 'config' },
          ]
        }
      ]
    },
    {
      module: 'Collaboration & Communication',
      subModules: [
        {
          subModule: 'Collaboration Tools',
          screens: [
            { screen: 'collaboration', name: 'Collaboration Hub', description: 'Team collaboration', category: 'collaborate' },
            { screen: 'chat', name: 'Team Chat', description: 'Agent messaging', category: 'collaborate' },
            { screen: 'mentions', name: 'Mentions & Tags', description: 'Notify team members', category: 'collaborate' },
          ]
        },
        {
          subModule: 'Mobile Access',
          screens: [
            { screen: 'mobile-access', name: 'Mobile App', description: 'Mobile features', category: 'access' },
            { screen: 'mobile-push', name: 'Push Notifications', description: 'Mobile alerts', category: 'access' },
          ]
        }
      ]
    },
    {
      module: 'Integration & Development',
      subModules: [
        {
          subModule: 'Integrations',
          screens: [
            { screen: 'integrations', name: 'Integrations Hub', description: 'Manage integrations', category: 'manage' },
            { screen: 'webhooks', name: 'Webhooks', description: 'Webhook management', category: 'manage' },
            { screen: 'api-keys', name: 'API Keys', description: 'Manage API keys', category: 'manage' },
            { screen: 'oauth-apps', name: 'OAuth Apps', description: 'Connected apps', category: 'manage' },
          ]
        },
        {
          subModule: 'API & Development',
          screens: [
            { screen: 'api-access', name: 'API Documentation', description: 'API docs', category: 'develop' },
            { screen: 'api-usage', name: 'API Usage', description: 'Track API calls', category: 'develop' },
            { screen: 'webhooks-config', name: 'Webhook Config', description: 'Configure webhooks', category: 'develop' },
          ]
        },
        {
          subModule: 'Workflow Configuration',
          screens: [
            { screen: 'workflow', name: 'Workflow Builder', description: 'Build workflows', category: 'configure' },
            { screen: 'workflow-templates', name: 'Workflow Templates', description: 'Template library', category: 'configure' },
          ]
        }
      ]
    },
    {
      module: 'AI & Automation',
      subModules: [
        {
          subModule: 'AI Features',
          screens: [
            { screen: 'ai-assistant', name: 'AI Assistant', description: 'Chat with AI', category: 'ai' },
            { screen: 'ai-auto-assign', name: 'Auto Assignment', description: 'AI routing', category: 'ai' },
            { screen: 'ai-suggestions', name: 'AI Suggestions', description: 'Smart suggestions', category: 'ai' },
            { screen: 'ai-categorization', name: 'Auto Categorization', description: 'Auto tag tickets', category: 'ai' },
          ]
        },
        {
          subModule: 'Automation',
          screens: [
            { screen: 'automation', name: 'Automation Rules', description: 'Configure automation', category: 'automate' },
            { screen: 'auto-response', name: 'Auto Responses', description: 'Auto-reply rules', category: 'automate' },
            { screen: 'smart-tags', name: 'Smart Tagging', description: 'AI tagging', category: 'automate' },
          ]
        }
      ]
    },
    {
      module: 'Support & Documentation',
      subModules: [
        {
          subModule: 'Help & Support',
          screens: [
            { screen: 'help-support', name: 'Help Center', description: 'Get support', category: 'support' },
            { screen: 'documentation', name: 'Documentation', description: 'User guides', category: 'support' },
            { screen: 'faq', name: 'FAQ', description: 'Common questions', category: 'support' },
          ]
        }
      ]
    }
  ];

  useEffect(() => {
    const tenantsData = getMockData('tenants');
    const foundTenant = tenantsData.tenants.find(t => t.id === Number(tenantId));
    
    if (foundTenant) {
      setTenant(foundTenant);
      
      // Initialize permissions for this tenant - handle new subModule structure
      const tenantPermissions = foundTenant.permissions || modules.flatMap(m => 
        m.subModules.flatMap(sm => 
          sm.screens.map(s => ({ ...s, module: m.module, subModule: sm.subModule, enabled: true }))
        )
      );
      
      setPermissions(tenantPermissions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, getMockData]);

  const togglePermission = (screenId) => {
    const updatedPermissions = permissions.map(p =>
      p.screen === screenId ? { ...p, enabled: !p.enabled } : p
    );
    setPermissions(updatedPermissions);
    setHasChanges(true);
  };


  const savePermissions = () => {
    if (!tenant) return;

    const tenantsData = getMockData('tenants');
    const updatedTenants = tenantsData.tenants.map(t =>
      t.id === Number(tenantId)
        ? { ...t, permissions: permissions, updatedAt: new Date().toISOString() }
        : t
    );

    updateMockData('tenants', { tenants: updatedTenants });
    setHasChanges(false);
    toast.success(`Permissions saved for ${tenant.name}`);
  };

  if (!tenant) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">Tenant not found</p>
        </div>
      </div>
    );
  }

  const enabledCount = permissions.filter(p => p.enabled).length;
  const totalCount = permissions.length;
  
  // Filter all permissions based on search, category, and module filters
  const filteredPermissions = permissions.filter(p => {
    const matchesSearch = searchTerm === '' || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    const matchesModule = moduleFilter === 'all' || p.module === moduleFilter;
    return matchesSearch && matchesCategory && matchesModule;
  });
  
  // Get unique categories and modules
  const categories = Array.from(new Set(permissions.map(p => p.category))).sort();
  const moduleNames = Array.from(new Set(permissions.map(p => p.module))).sort();
  
  // Group filtered permissions by module
  const groupedPermissions = moduleNames.reduce((acc, moduleName) => {
    const modulePerms = filteredPermissions.filter(p => p.module === moduleName);
    if (modulePerms.length > 0) {
      acc.push({ module: moduleName, permissions: modulePerms });
    }
    return acc;
  }, []);
  
  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="h-8 w-8 text-blue-600" />
            Tenant Permissions
          </h1>
          <p className="text-gray-600 mt-1">Configure module access for <span className="font-semibold text-gray-900">{tenant.name}</span></p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm text-gray-600">
              {enabledCount} of {totalCount} screens enabled
            </div>
            <Badge variant={enabledCount > 0 ? 'default' : 'secondary'}>
              {Math.round((enabledCount / totalCount) * 100)}% Enabled
            </Badge>
          </div>
          <Button 
            onClick={savePermissions}
            disabled={!hasChanges}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Save className="h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </div>
      
      {/* Search and Filter */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Modules</option>
                {moduleNames.map(mod => (
                  <option key={mod} value={mod}>{mod}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Results summary */}
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-gray-600">
            <span>Showing <strong>{filteredPermissions.length}</strong> of <strong>{totalCount}</strong> permissions</span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const updated = filteredPermissions.map(p => ({ ...p, enabled: true }));
                  const other = permissions.filter(p => !filteredPermissions.includes(p));
                  setPermissions([...updated, ...other]);
                  setHasChanges(true);
                  toast.success('All filtered permissions enabled');
                }}
                className="text-xs"
              >
                Enable All Shown
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const updated = filteredPermissions.map(p => ({ ...p, enabled: false }));
                  const other = permissions.filter(p => !filteredPermissions.includes(p));
                  setPermissions([...updated, ...other]);
                  setHasChanges(true);
                  toast.success('All filtered permissions disabled');
                }}
                className="text-xs"
              >
                Disable All Shown
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('all');
                  setModuleFilter('all');
                }}
                className="text-xs"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Permissions Grid - Unified View */}
      <div className="space-y-6">
        {groupedPermissions.map((group, idx) => {
          const moduleEnabledCount = group.permissions.filter(p => p.enabled).length;
          const moduleTotalCount = group.permissions.length;
          
          return (
            <div key={idx} className="space-y-4">
              {/* Module Header */}
              <div className="flex items-center justify-between pb-2 border-b-2 border-gray-200">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-gray-900">{group.module}</h2>
                  <Badge variant="outline" className="text-sm">
                    {moduleEnabledCount} / {moduleTotalCount} enabled
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated = permissions.map(p => 
                        p.module === group.module ? { ...p, enabled: true } : p
                      );
                      setPermissions(updated);
                      setHasChanges(true);
                    }}
                    className="text-xs"
                  >
                    Enable All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated = permissions.map(p => 
                        p.module === group.module ? { ...p, enabled: false } : p
                      );
                      setPermissions(updated);
                      setHasChanges(true);
                    }}
                    className="text-xs"
                  >
                    Disable All
                  </Button>
                </div>
              </div>
              
              {/* Permissions Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                {group.permissions.map((p) => {
                  const CategoryIcon = categoryIcons[p.category] || Settings;
                  const isEnabled = p.enabled;
                  
                  return (
                    <div
                      key={p.screen}
                      className={`p-4 border-2 rounded-lg transition-all cursor-pointer hover:shadow-lg group ${
                        isEnabled
                          ? 'border-green-400 bg-gradient-to-br from-green-50 to-green-100/50'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                      onClick={() => togglePermission(p.screen)}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <CategoryIcon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                          isEnabled ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm text-gray-900 mb-1 leading-tight">{p.name}</h4>
                          <p className="text-xs text-gray-600 line-clamp-2">{p.description}</p>
                        </div>
                        <div className="ml-1">
                          {isEnabled ? (
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span className={`text-xs font-semibold ${
                          isEnabled ? 'text-green-700' : 'text-gray-500'
                        }`}>
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <Badge 
                          variant={p.category === 'view' ? 'secondary' : 'outline'} 
                          className="text-xs flex items-center gap-1"
                        >
                          <CategoryIcon className="h-3 w-3" />
                          {p.category}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        
        {/* Show message if no results */}
        {filteredPermissions.length === 0 && (
          <div className="text-center py-12">
            <Filter className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No permissions match your filters</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setModuleFilter('all');
              }}
              className="mt-4"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantPermissions;

